/**
 * @fileoverview Утилиты для обновления mention во всех документах
 */

import { createFieldsMap, findFieldValue } from "./fieldMatcher";
import {
  numberToWords,
  extractBaseKeyFromCalc,
} from "@/shared/lib/numberToWords";

/**
 * Обновляет все mention узлы в JSON документе
 * @param {Object} documentJson - JSON структура документа TipTap
 * @param {Array} fields - Массив полей с данными для подстановки
 * @returns {Object} - Обновленный JSON документ
 */
export function updateMentionsInDocument(documentJson, fields, lang = "ru") {
  if (!documentJson || !documentJson.content) {
    console.warn("updateMentionsInDocument: invalid document JSON");
    return documentJson;
  }

  if (!Array.isArray(fields) || fields.length === 0) {
    console.warn("updateMentionsInDocument: no fields provided");
    return documentJson;
  }

  // Создаем карту для быстрого поиска
  const fieldsMap = createFieldsMap(fields);

  if (!fieldsMap.size) {
    console.warn("updateMentionsInDocument: fields map is empty");
    return documentJson;
  }

  /**
   * Рекурсивно обходит узлы документа и обновляет mention
   */
  function traverseAndUpdate(node) {
    if (!node || typeof node !== "object") return node;

    // Если это mention узел - обновляем его
    if (node.type === "mention" && node.attrs) {
      const attrs = node.attrs;

      // Обработка isNumberToWords (calc) mentions
      if (attrs.isNumberToWords) {
        const baseKey = extractBaseKeyFromCalc(attrs.key);

        if (baseKey) {
          const matchInfo = findFieldValue(
            fieldsMap,
            attrs.service_code,
            baseKey,
            attrs.participant_type_code
          );

          if (matchInfo && matchInfo.value) {
            const wordsValue = numberToWords(matchInfo.value, lang);

            node.attrs = {
              ...attrs,
              value: wordsValue,
            };
          }
        }
        return node;
      }

      // Ищем соответствие в полученных данных
      const matchInfo = findFieldValue(
        fieldsMap,
        attrs.service_code,
        attrs.key,
        attrs.participant_type_code
      );

      // ВАЖНО: matchInfo существует если ключ найден в ответе сервиса
      if (matchInfo) {
        // Ключ найден в ответе сервиса
        const newValue = matchInfo.value || "";

        node.attrs = {
          ...attrs,
          value: newValue,
        };
      }
    }

    // Если есть дочерние узлы - обрабатываем их рекурсивно
    if (Array.isArray(node.content)) {
      node.content = node.content.map((childNode) =>
        traverseAndUpdate(childNode)
      );
    }

    return node;
  }

  // Создаем глубокую копию чтобы не мутировать оригинал
  const updatedJson = JSON.parse(JSON.stringify(documentJson));

  // Обновляем документ
  traverseAndUpdate(updatedJson);

  return updatedJson;
}

/**
 * Обновляет mention во всех языковых версиях документов
 * @param {Object} form - Объект формы с content, content_kz, content_en
 * @param {Array} fields - Массив полей для подстановки
 * @returns {Object} - Обновленная форма со всеми документами
 */
export function updateMentionsInAllLanguages(form, fields) {
  if (!form || typeof form !== "object") {
    console.error("updateMentionsInAllLanguages: invalid form");
    return form;
  }

  if (!Array.isArray(fields) || fields.length === 0) {
    console.warn("updateMentionsInAllLanguages: no fields provided");
    return form;
  }

  const updatedForm = { ...form };
  const languages = [
    { key: "content", lang: "ru" },
    { key: "content_kz", lang: "kz" },
    { key: "content_en", lang: "en" },
  ];

  languages.forEach(({ key, lang }) => {
    const documentJson = updatedForm[key];

    if (documentJson && typeof documentJson === "object") {
      updatedForm[key] = updateMentionsInDocument(documentJson, fields, lang);
    }
  });

  return updatedForm;
}

/**
 * Подсчитывает количество mention узлов в документе
 * @param {Object} documentJson - JSON структура документа
 * @returns {Object} - Статистика по mention
 */
export function countMentionsInDocument(documentJson) {
  if (!documentJson || !documentJson.content) {
    return { total: 0, withValue: 0, withoutValue: 0 };
  }

  let total = 0;
  let withValue = 0;
  let withoutValue = 0;

  function traverse(node) {
    if (!node || typeof node !== "object") return;

    if (node.type === "mention" && node.attrs) {
      total++;
      const hasValue = node.attrs.value && node.attrs.value.trim() !== "";
      if (hasValue) {
        withValue++;
      } else {
        withoutValue++;
      }
    }

    if (Array.isArray(node.content)) {
      node.content.forEach((childNode) => traverse(childNode));
    }
  }

  traverse(documentJson);

  return { total, withValue, withoutValue };
}
